import { DocUpdateResult } from '@common/collab/types/doc-update.type';
import { CollabStep } from '@common/prosemirror/transform/collab-step.type';
import { StepJSON } from '@common/prosemirror/transform/json';
import { ProseMirrorNode, Schema } from 'prosemirror-model';
import { Step } from 'prosemirror-transform';

export function applyStepsToDoc(doc: ProseMirrorNode, steps: CollabStep[], schemaVersion: string, docVersion: number, clientId: string, userId: string): DocUpdateResult {
  const appliedAt = new Date().toISOString();
  const invertedSteps: CollabStep[] = [];

  // Apply the steps to the doc
  for (let i = 0; i < steps.length; i += 1) {
    let step = steps[i];

    step = toCollabStep(step, {
      appliedAt,
      clientID: clientId,
      docVersion: docVersion + i,
      externalCommit: step.externalCommit,
      restoredDocVersion: step.restoredDocVersion,
      restoredFrom: step.restoredFrom,
      schemaVersion,
      transactionId: step.transactionId,
      userID: userId
    });

    invertedSteps.push(toCollabStep(step.invert(doc), step));

    doc = step.apply(doc).doc;
  }

  // Return the results
  return {
    doc,
    invertedSteps,
    version: docVersion + steps.length
  }
}

function toCollabStep(step: Step, stepMetaData: Omit<CollabStep, keyof Step>): CollabStep {
  const collabStep = step as CollabStep;

  // Copy over the step meta data
  collabStep.appliedAt = stepMetaData.appliedAt;
  collabStep.clientID = stepMetaData.clientID;
  collabStep.docVersion = stepMetaData.docVersion;
  collabStep.externalCommit = stepMetaData.externalCommit;
  collabStep.restoredDocVersion = stepMetaData.restoredDocVersion;
  collabStep.restoredFrom = stepMetaData.restoredFrom;
  collabStep.schemaVersion = stepMetaData.schemaVersion;
  collabStep.transactionId = stepMetaData.transactionId;
  collabStep.userID = stepMetaData.userID;

  return collabStep;
}

export function addTransactionIdToSteps(steps: Step[], transactionId: string) {
  steps.forEach((step: CollabStep) => {
    step.transactionId = transactionId;
  });
}

export function stepFromJSON(schema: Schema, json: StepJSON): CollabStep {
  const step = Step.fromJSON(schema, json) as CollabStep;
  step.appliedAt = json.appliedAt;
  step.clientID = json.clientID;
  step.docVersion = json.docVersion;
  step.externalCommit = json.externalCommit;
  step.restoredDocVersion = json.restoredDocVersion;
  step.restoredFrom = json.restoredFrom;
  step.schemaVersion = json.schemaVersion;
  step.transactionId = json.transactionId;
  step.userID = json.userID;
  return step;
}

export function stepsFromJSON(schema: Schema, steps: StepJSON[]): CollabStep[] {
  return Array.isArray(steps) ? steps.map(json => stepFromJSON(schema, json)) : [];
}

export function stepsAsJSON(steps: CollabStep[]): StepJSON[] {
  return steps.map<StepJSON>(step => {
    return {
      ...step.toJSON(),
      appliedAt: step.appliedAt,
      clientID: step.clientID,
      docVersion: step.docVersion,
      externalCommit: step.externalCommit,
      restoredDocVersion: step.restoredDocVersion,
      restoredFrom: step.restoredFrom,
      schemaVersion: step.schemaVersion,
      transactionId: step.transactionId,
      userID: step.userID
    };
  });
}
